/**
 * Fetches the short-lived token an enquiry form sends with its submission.
 *
 * The server side and the reasoning are in `lib/server/form-token.ts`: a
 * scanner posts straight at `/api/fde` without ever loading a page, so a
 * token proves the submission came from one.
 *
 * Fetched when a form is first submitted rather than when the page loads: it
 * costs a request, and most visitors never send a form. The result is cached
 * for the life of the page, because one token is good for two hours and a
 * person does not fill in two forms in different hours without a reload.
 *
 * **It never throws and never blocks a submission.** If the request fails,
 * the form posts without a token and the server decides what to do with it.
 * A network hiccup must not be the reason an enquiry is lost.
 */

let cached: Promise<string | null> | null = null;

export function formToken(): Promise<string | null> {
  if (!cached) {
    cached = fetch("/api/form-token", { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { token?: string | null } | null) => body?.token ?? null)
      .catch(() => null);
  }
  return cached;
}

/**
 * Drops the cached token, so the next submission fetches a fresh one. Called
 * when the server says the token was stale, which happens to a page left open
 * past the token's life.
 */
export function clearFormToken(): void {
  cached = null;
}
